import type { ProjectTreeNode } from 'types/api.types';
import isUndefined from 'lodash/isUndefined';
import isEmpty from 'lodash/isEmpty';
export interface HierarchySegment {
  id: string;
  name: string;
}

export const getAncestorIds = (
  projects: Record<string, Pick<ProjectTreeNode, 'parentProjectId'>>,
  projectId: string
): string[] => {
  const ids: string[] = [];
  const visited = new Set<string>();
  let current: string | undefined = projectId;
  while (current && projects[current] && !visited.has(current)) {
    visited.add(current);
    ids.push(current);
    current = projects[current].parentProjectId;
  }
  return ids.reverse(); // root → self
};

// self → every descendant. A project missing from the tree still counts as its own subtree.
export const getSubTreeIds = (projects: Record<string, ProjectTreeNode>, projectId: string): string[] => {
  const collect = (node: ProjectTreeNode): string[] => [node.id, ...(node.children ?? []).flatMap(collect)];
  const project = projects[projectId];

  return project ? collect(project) : [projectId];
};

export const isParentProject = (project: ProjectTreeNode) => !isEmpty(project?.children);
export const isSubProject = (project: ProjectTreeNode) => !isUndefined(project?.parentProjectId);
export const getIdsOfProjectHierarchy = (projects: Record<string, ProjectTreeNode>, projectId: string) =>
  getAncestorIds(projects, projectId);
export const getRedirectUrlForProject = (project: Pick<ProjectTreeNode, 'id'>) => {
  return `/p/${project.id}/environments`;
};

export const getProjectNameHierarchy = (projectId: string, projects: Record<string, ProjectTreeNode>) => {
  const project = projects[projectId];
  if (!project) return null;
  const hierarchy = getIdsOfProjectHierarchy(projects, projectId);
  return hierarchy.map(id => projects[id]?.name ?? id).join(' / ');
};

export const getHierarchySegments = (
  projectId: string,
  projects: Record<string, ProjectTreeNode>
): { segments: HierarchySegment[] } => {
  const project = projects[projectId];
  if (!project) {
    return { segments: [] };
  }

  const ids = getIdsOfProjectHierarchy(projects, projectId) || [];
  const segments = ids.map(pid => ({ id: pid, name: projects[pid]?.name ?? pid }));
  return { segments };
};
